import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Book, Gamepad2, Compass, Star, Palette, Trophy, Map, Store,
  Sparkles, Flame, ChevronRight, Zap, Clock, Users, Shield,
  BookOpen, Target, Award, Wand2, Heart, Pen, Quote, UserPlus, Rocket, GraduationCap,
  Brain, BarChart3, ArrowRight
} from "lucide-react";
import { useApp, API, IMG } from "../../context";
import {
  DayNightCycle, ParallaxStars, ForegroundSparkles,
  ShootingStars, PortalTransition
} from "../../components/shared";
import ThemeSpecialEffects from "../../components/ThemeSpecialEffects";
import InteractiveSkyGame from "../../components/InteractiveSkyGame";
import WeatherOverlay from "../../components/WeatherOverlay";
import NighttimeEnhancements from "../../components/NighttimeEnhancements";
import WelcomeBackSequence from "../../components/WelcomeBackSequence";
import { IsaiahMascotCore, useIsaiah, getWorldPose } from "../../components/isaiah";
import { useABTest } from "../../hooks/useABTest";

const MouseSparkleTrail = () => {
  const [sparkles, setSparkles] = useState([]);
  const idRef = useRef(0);
  useEffect(() => {
    const handler = (e) => {
      if (Math.random() > 0.3) return;
      const id = ++idRef.current;
      const colors = ["#FBBF24", "#A78BFA", "#34D399", "#F472B6", "#38BDF8", "#FB923C"];
      setSparkles(prev => [...prev.slice(-8), { id, x: e.clientX, y: e.clientY, color: colors[id % colors.length], size: 4 + Math.random() * 6 }]);
      setTimeout(() => setSparkles(prev => prev.filter(s => s.id !== id)), 800);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);
  return (
    <div className="fixed inset-0 pointer-events-none z-[999]">
      {sparkles.map(s => (
        <div key={s.id} className="absolute" style={{
          left: s.x - s.size / 2, top: s.y - s.size / 2,
          width: s.size, height: s.size, borderRadius: "50%",
          background: s.color, boxShadow: `0 0 ${s.size * 2}px ${s.color}`,
          animation: "mouse-sparkle 0.8s ease-out forwards",
        }} />
      ))}
    </div>
  );
};

/* Theme-aware Animated Color Orbs */
const AnimatedOrbs = ({ orbColors }) => {
  const colors = orbColors || [
    "rgba(167,139,250,0.4)", "rgba(251,146,60,0.35)",
    "rgba(236,72,153,0.3)", "rgba(34,211,238,0.35)", "rgba(52,211,153,0.3)"
  ];
  const configs = [
    { size: 350, top: "-5%", right: "-5%", anim: "orb-float-1 25s ease-in-out infinite" },
    { size: 300, bottom: "5%", left: "-8%", anim: "orb-float-2 30s ease-in-out infinite" },
    { size: 250, top: "30%", left: "60%", anim: "orb-float-3 35s ease-in-out infinite" },
    { size: 280, top: "50%", right: "15%", anim: "orb-float-1 28s ease-in-out infinite reverse" },
    { size: 200, top: "10%", left: "20%", anim: "orb-float-2 22s ease-in-out infinite reverse" },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]">
      {configs.map((c, i) => (
        <div key={i} className="sky-orb" style={{
          width: c.size, height: c.size,
          top: c.top, bottom: c.bottom, left: c.left, right: c.right,
          background: `radial-gradient(circle, ${colors[i]}, ${colors[i].replace(/[\d.]+\)$/, '0.05)')}, transparent 70%)`,
          animation: c.anim,
        }} />
      ))}
    </div>
  );
};

const LightRays = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[3]">
    <div className="light-ray" style={{ left: "10%", animationDelay: "0s" }} />
    <div className="light-ray" style={{ left: "35%", animationDelay: "-5s" }} />
    <div className="light-ray" style={{ left: "60%", animationDelay: "-10s" }} />
    <div className="light-ray" style={{ left: "85%", animationDelay: "-15s" }} />
  </div>
);

const WaveRibbons = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]">
    <svg className="absolute w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 800">
      <defs>
        <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(139,92,246,0)" /><stop offset="30%" stopColor="rgba(139,92,246,0.15)" /><stop offset="50%" stopColor="rgba(236,72,153,0.2)" /><stop offset="70%" stopColor="rgba(251,146,60,0.15)" /><stop offset="100%" stopColor="rgba(251,146,60,0)" />
        </linearGradient>
        <linearGradient id="wave2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(34,211,238,0)" /><stop offset="30%" stopColor="rgba(34,211,238,0.12)" /><stop offset="50%" stopColor="rgba(59,130,246,0.18)" /><stop offset="70%" stopColor="rgba(139,92,246,0.12)" /><stop offset="100%" stopColor="rgba(167,139,250,0)" />
        </linearGradient>
      </defs>
      <path fill="url(#wave1)" opacity="0.6">
        <animate attributeName="d" dur="18s" repeatCount="indefinite" values="M0,200 C240,120 480,280 720,200 C960,120 1200,280 1440,200 L1440,320 C1200,400 960,240 720,320 C480,400 240,240 0,320 Z;M0,250 C240,170 480,330 720,250 C960,170 1200,330 1440,250 L1440,370 C1200,450 960,290 720,370 C480,450 240,290 0,370 Z;M0,200 C240,120 480,280 720,200 C960,120 1200,280 1440,200 L1440,320 C1200,400 960,240 720,320 C480,400 240,240 0,320 Z" />
      </path>
      <path fill="url(#wave2)" opacity="0.5">
        <animate attributeName="d" dur="22s" repeatCount="indefinite" values="M0,450 C240,370 480,530 720,450 C960,370 1200,530 1440,450 L1440,570 C1200,650 960,490 720,570 C480,650 240,490 0,570 Z;M0,400 C240,320 480,480 720,400 C960,320 1200,480 1440,400 L1440,520 C1200,600 960,440 720,520 C480,600 240,440 0,520 Z;M0,450 C240,370 480,530 720,450 C960,370 1200,530 1440,450 L1440,570 C1200,650 960,490 720,570 C480,650 240,490 0,570 Z" />
      </path>
    </svg>
  </div>
);

const FloatingPlanets = ({ showPlanets }) => {
  if (showPlanets === false) return null;
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 4 }}>
      {/* Large purple planet with rings, top right, fully visible below nav */}
      <div className="absolute" style={{ top: "10%", right: "5%", animation: "planet-orbit 90s ease-in-out infinite" }}>
        <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full" style={{ background: "radial-gradient(circle at 32% 32%, #E9D5FF 0%, #A78BFA 30%, #6D28D9 70%, #3B0764 100%)", boxShadow: "0 0 60px rgba(167,139,250,0.5), 0 0 120px rgba(109,40,217,0.25), inset -10px -10px 30px rgba(0,0,0,0.4), inset 4px 4px 15px rgba(255,255,255,0.1)", opacity: 0.75 }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 sm:w-64 h-8 rounded-full border-2 border-purple-300/25 -rotate-12" style={{ boxShadow: "0 0 20px rgba(167,139,250,0.15)" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[42%] w-48 sm:w-56 h-5 rounded-full border border-purple-200/15 -rotate-12" />
      </div>
      {/* Golden planet, moved higher */}
      <div className="absolute" style={{ bottom: "32%", left: "3%", animation: "planet-orbit 70s ease-in-out infinite reverse" }}>
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" style={{ background: "radial-gradient(circle at 38% 28%, #FEF3C7 0%, #FBBF24 35%, #B45309 75%, #78350F 100%)", boxShadow: "0 0 50px rgba(251,191,36,0.45), 0 0 100px rgba(180,83,9,0.2), inset -6px -6px 20px rgba(0,0,0,0.35), inset 3px 3px 10px rgba(255,255,255,0.15)", opacity: 0.7 }} />
        <div className="absolute top-[35%] left-[20%] w-4 h-3 rounded-full bg-amber-800/20" />
        <div className="absolute top-[55%] left-[50%] w-3 h-2 rounded-full bg-amber-900/15" />
      </div>
      {/* Teal/cyan small planet, mid right */}
      <div className="absolute" style={{ top: "50%", right: "8%", animation: "planet-orbit 110s ease-in-out -40s infinite" }}>
        <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full" style={{ background: "radial-gradient(circle at 35% 30%, #CFFAFE 0%, #06B6D4 40%, #0E7490 75%, #164E63 100%)", boxShadow: "0 0 40px rgba(6,182,212,0.5), 0 0 80px rgba(14,116,144,0.2), inset -4px -4px 15px rgba(0,0,0,0.35), inset 2px 2px 8px rgba(255,255,255,0.1)", opacity: 0.65 }} />
      </div>
    </div>
  );
};

const BoldSparkles = ({ isNight = false, sparkleColors }) => {
  const count = isNight ? 60 : 40;
  const defaultColors = ["#fff", "#C4B5FD", "#FDE68A", "#A5F3FC", "#FBCFE8", "#BBF7D0"];
  const colors = sparkleColors || defaultColors;
  const stars = useMemo(() => Array.from({ length: count }, (_, i) => ({
    x: Math.random() * 100, y: Math.random() * 100,
    size: isNight ? 2 + Math.random() * 5 : 2 + Math.random() * 4,
    delay: Math.random() * 6, dur: 2 + Math.random() * 3,
    color: colors[i % colors.length],
  })), [count, isNight, colors]);
  return (
    <div className="absolute inset-0 pointer-events-none z-[3]">
      {stars.map((s, i) => (
        <div key={i} className="absolute" style={{
          width: s.size, height: s.size, top: `${s.y}%`, left: `${s.x}%`,
          background: s.color, borderRadius: "50%",
          boxShadow: `0 0 ${s.size * 3}px ${s.color}, 0 0 ${s.size * 6}px ${s.color}50`,
          animation: `star-pulse ${s.dur}s ease-in-out ${s.delay}s infinite`,
        }} />
      ))}
    </div>
  );
};

const HorizonGlow = ({ color }) => (
  <div className="absolute bottom-0 left-0 w-full h-[35%] pointer-events-none z-[1] overflow-hidden">
    <div className="absolute bottom-0 left-0 w-full h-full" style={{
      background: color
        ? `linear-gradient(0deg, ${color} 0%, ${color.replace(/[\d.]+\)$/, '0.08)')} 60%, transparent 100%)`
        : "linear-gradient(0deg, rgba(251,146,60,0.25) 0%, rgba(236,72,153,0.15) 30%, rgba(139,92,246,0.08) 60%, transparent 100%)",
      animation: "aurora-shift 25s ease-in-out infinite"
    }} />
  </div>
);

const PulsingRings = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]">
    {[0, 1, 2].map(i => (
      <div key={i} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border" style={{
        width: `${300 + i * 200}px`, height: `${300 + i * 200}px`,
        borderColor: ["rgba(139,92,246,0.12)", "rgba(236,72,153,0.08)", "rgba(34,211,238,0.06)"][i],
        animation: `ring-pulse ${8 + i * 4}s ease-in-out ${i * -2}s infinite`,
      }} />
    ))}
  </div>
);

const GradientMesh = () => (
  <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
    <div className="absolute w-full h-full" style={{
      background: `radial-gradient(ellipse 80% 50% at 20% 30%, rgba(139,92,246,0.15), transparent), radial-gradient(ellipse 60% 60% at 80% 20%, rgba(236,72,153,0.12), transparent), radial-gradient(ellipse 70% 50% at 50% 80%, rgba(251,146,60,0.10), transparent), radial-gradient(ellipse 50% 40% at 70% 60%, rgba(34,211,238,0.10), transparent)`,
      animation: "mesh-shift 30s ease-in-out infinite",
    }} />
  </div>
);

const MeteorTrails = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[4]">
    {[0, 1, 2, 3, 4].map(i => (
      <div key={i} className="meteor-trail" style={{ top: `${3 + i * 18}%`, left: `${-10 + i * 22}%`, animationDelay: `${i * 5}s` }} />
    ))}
  </div>
);

// Big white comet that sweeps across the screen every 20-30s
const GiantComet = () => {
  const [visible, setVisible] = useState(false);
  const [fromTop, setFromTop] = useState(15);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const triggerComet = () => {
      setFromTop(8 + Math.random() * 50);
      setVisible(true);
      setTimeout(() => setVisible(false), 3500);
      const next = 20000 + Math.random() * 15000;
      timeoutRef.current = setTimeout(triggerComet, next);
    };
    timeoutRef.current = setTimeout(triggerComet, 5000 + Math.random() * 5000);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  if (!visible) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
      <div style={{
        position: "absolute",
        top: `${fromTop}%`,
        left: "-15%",
        width: "220px",
        height: "3px",
        background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 10%, rgba(255,255,255,0.9) 60%, #fff 85%, rgba(200,220,255,0.95) 100%)",
        borderRadius: "2px",
        boxShadow: "0 0 30px rgba(255,255,255,0.8), 0 0 60px rgba(200,220,255,0.5), 0 0 100px rgba(139,92,246,0.3)",
        animation: "comet-sweep 3.2s ease-in forwards",
        transform: "rotate(-12deg)",
      }}>
        {/* Comet head glow */}
        <div style={{
          position: "absolute",
          right: "-6px",
          top: "-9px",
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background: "radial-gradient(circle, #fff 0%, rgba(200,220,255,0.8) 40%, rgba(139,92,246,0.3) 70%, transparent 100%)",
          boxShadow: "0 0 25px rgba(255,255,255,0.9), 0 0 50px rgba(200,220,255,0.6), 0 0 80px rgba(139,92,246,0.4)",
        }} />
        {/* Secondary tail */}
        <div style={{
          position: "absolute",
          right: "10px",
          top: "-1px",
          width: "300px",
          height: "5px",
          background: "linear-gradient(270deg, transparent 0%, rgba(139,92,246,0.2) 40%, rgba(236,72,153,0.15) 70%, transparent 100%)",
          borderRadius: "3px",
          transform: "translateX(-100%)",
          filter: "blur(2px)",
        }} />
      </div>
    </div>
  );
};

// (Brown comet removed per user request)

const Nebulas = () => (
  <div className="nebula-layer">
    <div className="nebula-blob" style={{ width: 400, height: 400, top: "10%", left: "-5%", background: "radial-gradient(circle, rgba(59,130,246,0.3), transparent)" }} />
    <div className="nebula-blob" style={{ width: 350, height: 350, top: "50%", right: "-10%", background: "radial-gradient(circle, rgba(139,92,246,0.25), transparent)" }} />
    <div className="nebula-blob" style={{ width: 300, height: 300, bottom: "5%", left: "30%", background: "radial-gradient(circle, rgba(236,72,153,0.2), transparent)" }} />
  </div>
);

const Fireflies = ({ count = 12 }) => (
  <div className="firefly-layer">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="firefly" style={{ top: `${15 + Math.random() * 70}%`, left: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 8}s` }} />
    ))}
  </div>
);

const CloudLayers = ({ opacity = 0.2 }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[3]">
    <div className="absolute rounded-full blur-3xl" style={{ width: 800, height: 130, top: "5%", left: "-15%", background: `rgba(255,255,255,${Math.min(opacity * 1.2, 0.3)})`, animation: "cloud-drift 55s linear infinite" }} />
    <div className="absolute rounded-full blur-3xl" style={{ width: 650, height: 110, top: "20%", right: "-20%", background: `rgba(255,255,255,${opacity})`, animation: "cloud-drift 70s linear infinite reverse" }} />
    <div className="absolute rounded-full blur-3xl" style={{ width: 500, height: 90, top: "40%", left: "10%", background: `rgba(255,255,255,${opacity * 0.9})`, animation: "cloud-drift 65s linear -25s infinite" }} />
    <div className="absolute rounded-full blur-3xl" style={{ width: 700, height: 100, top: "60%", right: "-10%", background: `rgba(255,255,255,${opacity * 0.75})`, animation: "cloud-drift 80s linear -40s infinite" }} />
  </div>
);

/* ===== CONSTELLATION PATHS between main world nodes ===== */
const ConstellationPaths = () => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none z-[9]" preserveAspectRatio="none" viewBox="0 0 100 100">
    <defs>
      <linearGradient id="path-glow" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="rgba(139,92,246,0)" /><stop offset="50%" stopColor="rgba(139,92,246,0.15)" /><stop offset="100%" stopColor="rgba(139,92,246,0)" />
      </linearGradient>
    </defs>
    {/* Connections: Learn↔Adventure, Adventure↔Arcade, Learn↔Art, Arcade↔Art, Adventure↔Art */}
    {[[20,42,50,24],[50,24,80,42],[20,42,50,76],[80,42,50,76],[50,24,50,76]].map(([x1,y1,x2,y2], i) => (
      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#path-glow)" strokeWidth="0.15" strokeDasharray="0.8 0.4" opacity="0.4">
        <animate attributeName="stroke-dashoffset" from="0" to="-2.4" dur={`${6 + i}s`} repeatCount="indefinite" />
      </line>
    ))}
    {/* Midpoint dots */}
    {[[35,33],[65,33],[35,59],[65,59],[50,50]].map(([cx,cy], i) => (
      <circle key={`dot-${i}`} cx={cx} cy={cy} r="0.3" fill="rgba(167,139,250,0.4)">
        <animate attributeName="opacity" values="0.2;0.8;0.2" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
      </circle>
    ))}
  </svg>
);

/* ===== WORLD MAP NODE (replaces FloatingIsland) ===== */
export { MouseSparkleTrail, AnimatedOrbs, LightRays, WaveRibbons, FloatingPlanets, BoldSparkles, HorizonGlow, PulsingRings, GradientMesh, MeteorTrails, GiantComet, Nebulas, Fireflies, CloudLayers, ConstellationPaths };
